import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import GetAccessModal from '@/components/landing/GetAccessModal';
import { ThemeContext } from '@/contexts/ThemeContext';
import { tLanding, type LandingLang, type LandingTranslationKey } from '@/i18n/landingTranslations';
import { LandingFooter } from '@/components/landingPrototype/LandingFooter';
import { FaqSidebar } from '@/components/landingPrototype/FaqSidebar';
import { LandingTopNav } from '@/components/landingPrototype/LandingTopNav';
import { LandingSeo } from '@/components/seo/LandingSeo';
import { highlightLandingText } from '@/components/landingPrototype/highlightLandingText';

import '@/styles/landingPrototype.css';
// (page-specific styles may be imported here)

const getCircleColor = (circleIndex: number) => {
  const colorIndex = (circleIndex - 2) % 4;
  switch (colorIndex) {
    case 0:
      return 'rgba(93, 99, 255, 0.8)';
    case 1:
      return 'rgba(138, 43, 226, 0.8)';
    case 2:
      return 'rgba(75, 192, 192, 0.8)';
    case 3:
      return 'rgba(255, 99, 132, 0.8)';
    default:
      return 'rgba(93, 99, 255, 0.8)';
  }
};

const ConcentricCirclesBackground: React.FC<{ enabled: boolean; reduceMotion: boolean }> = ({ enabled, reduceMotion }) => {
  const circles = useMemo(() => {
    const circleCount = reduceMotion ? 8 : 14; // fewer circles for heavy browsers
    const baseRadius = 200;
    const maxRadius = 3000;
    const increasedDifference = (maxRadius - baseRadius) * 1.3;
    return Array.from({ length: circleCount }, (_, i) => {
      const radius = baseRadius + increasedDifference * (i / (circleCount - 1));
      return { index: i + 2, radius, stretchIndex: i };
    });
  }, []);

  const circleRefs = useRef<Array<HTMLDivElement | null>>([]);
  const targetPositionRef = useRef({ x: 50, y: 50 });
  const currentPositionRef = useRef({ x: 50, y: 50 });
  const rafRef = useRef<number | null>(null);
  const isAnimatingRef = useRef(false);

  useEffect(() => {
    if (!enabled || reduceMotion) return undefined;

    const circle1Radius = 10.7;
    const circle2Radius = 35.7;
    const maxDistance = circle2Radius - circle1Radius - 1;

    const apply = () => {
      const offsetX = currentPositionRef.current.x - 50;
      const offsetY = currentPositionRef.current.y - 50;
      const dist = Math.sqrt(offsetX * offsetX + offsetY * offsetY);
      const ratio = dist > maxDistance ? maxDistance / dist : 1;
      const limitedOffsetX = offsetX * ratio;
      const limitedOffsetY = offsetY * ratio;

      const stretchDistance = Math.sqrt(limitedOffsetX * limitedOffsetX + limitedOffsetY * limitedOffsetY);
      const originX = limitedOffsetX > 0 ? 'left' : limitedOffsetX < 0 ? 'right' : 'center';
      const originY = limitedOffsetY > 0 ? 'top' : limitedOffsetY < 0 ? 'bottom' : 'center';

      for (let i = 0; i < circles.length; i++) {
        const el = circleRefs.current[i];
        if (!el) continue;
        const stretch = Math.min((stretchDistance / 40) * (1 + circles[i].stretchIndex * 0.15), 1.2);
        const sx = 1 + (stretch * Math.abs(limitedOffsetX)) / 100;
        const sy = 1 + (stretch * Math.abs(limitedOffsetY)) / 100;
        el.style.setProperty('--stretch-x', String(sx));
        el.style.setProperty('--stretch-y', String(sy));
        el.style.setProperty('--origin-x', originX);
        el.style.setProperty('--origin-y', originY);
      }
    };

    const tick = () => {
      isAnimatingRef.current = true;

      const prev = currentPositionRef.current;
      const dx = targetPositionRef.current.x - prev.x;
      const dy = targetPositionRef.current.y - prev.y;
      const isReturning =
        Math.abs(targetPositionRef.current.x - 50) < 1 && Math.abs(targetPositionRef.current.y - 50) < 1;
      const speed = isReturning ? 0.05 : 0.08;
      const next =
        Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01
          ? targetPositionRef.current
          : { x: prev.x + dx * speed, y: prev.y + dy * speed };
      currentPositionRef.current = next;

      apply();

      // Stop loop when close enough (reduces idle CPU usage).
      if (Math.abs(dx) < 0.05 && Math.abs(dy) < 0.05) {
        isAnimatingRef.current = false;
        rafRef.current = null;
        return;
      }

      rafRef.current = window.requestAnimationFrame(tick);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const deltaX = e.clientX - centerX;
      const deltaY = e.clientY - centerY;
      const maxOffset = Math.min(window.innerWidth, window.innerHeight) * 0.5;
      const dist = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const r = dist > maxOffset ? maxOffset / dist : 1;
      const limitedDeltaX = deltaX * r;
      const limitedDeltaY = deltaY * r;
      const offsetX = limitedDeltaX * 0.3;
      const offsetY = limitedDeltaY * 0.3;
      targetPositionRef.current = {
        x: 50 + (offsetX / window.innerWidth) * 100,
        y: 50 + (offsetY / window.innerHeight) * 100,
      };

      if (!isAnimatingRef.current) {
        rafRef.current = window.requestAnimationFrame(tick);
      }
    };

    const handleMouseLeave = () => {
      targetPositionRef.current = { x: 50, y: 50 };
      if (!isAnimatingRef.current) rafRef.current = window.requestAnimationFrame(tick);
    };

    // Initial apply
    apply();

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [enabled, circles]);

  const showGlows = enabled;
  const particleCount = enabled ? (reduceMotion ? 2 : 3) : 0;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      <div
        className={`concentric-circles${reduceMotion ? ' reduced-motion' : ''}`}
        style={{
          position: 'absolute',
          top: '50%',
          left: '150%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '200vh',
          zIndex: 0,
        }}
      >
          {circles.map((circle, circleIdx) => {
          const color = getCircleColor(circle.index);
          const borderColor = color.replace('0.8', '0.22');
          const glow1 = color.replace('0.8', '0.18');
          const glow2 = color.replace('0.8', '0.10');
          const glow3 = color.replace('0.8', '0.06');
          return (
            <div
              key={circle.index}
              ref={el => {
                circleRefs.current[circleIdx] = el;
              }}
              className={`circle circle-${circle.index}`}
              style={
                {
                  '--mouse-x': '50%',
                  '--mouse-y': '50%',
                  '--stretch-x': '1',
                  '--stretch-y': '1',
                  '--origin-x': 'center',
                  '--origin-y': 'center',
                  width: `${circle.radius * 2}px`,
                  height: `${circle.radius * 2}px`,
                  borderColor,
                  boxShadow: showGlows ? `0 0 20px ${glow1}, 0 0 40px ${glow2}, 0 0 60px ${glow3}` : 'none',
                } as React.CSSProperties
              }
            >
              {particleCount > 0
                ? Array.from({ length: particleCount }).map((_, idx) => {
                    const angleDeg = idx * 120;
                    const baseDuration = reduceMotion ? 26 : 12;
                    const duration = baseDuration + circle.stretchIndex * (reduceMotion ? 0.6 : 0.25) + idx * (reduceMotion ? 1.4 : 0.8);
                    const reverse = idx % 2 === 1;
                    const timingSteps = Math.max(24, Math.round(duration * 24));
                    return (
                      <span
                        key={idx}
                        className={`orbiting-particle${reverse ? ' reverse' : ''}`}
                        style={
                          {
                            '--orbit-radius': `${circle.radius}px`,
                            '--orbit-angle': `${angleDeg}deg`,
                            '--orbit-duration': `${duration}s`,
                            backgroundColor: color,
                            boxShadow: `0 0 16px ${color}`,
                            animationTimingFunction: reduceMotion ? `steps(${timingSteps})` : 'linear',
                          } as React.CSSProperties
                        }
                      />
                    );
                  })
                : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const UnifiedApiLandingPage: React.FC = () => {
  const { isDark, setIsDark } = useContext(ThemeContext);
  const [isAccessOpen, setIsAccessOpen] = useState(false);
  const [isFaqCollapsed, setIsFaqCollapsed] = useState(true);
  const docsUrl = `https://api.swarmind.ai/docs?theme=${isDark ? 'dark' : 'light'}#/`;

  const modelsSectionRef = useRef<HTMLElement | null>(null);
  const modelsCarouselRef = useRef<HTMLDivElement | null>(null);
  const motionEnabled = useMemo(() => {
    try {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      const coarse = window.matchMedia?.('(pointer: coarse)')?.matches;
      return !reduce && !coarse;
    } catch {
      return true;
    }
  }, []);
  const reduceMotion = useMemo(() => {
    try {
      return /firefox/i.test(navigator.userAgent);
    } catch {
      return false;
    }
  }, []);
  const [whyVisible, setWhyVisible] = useState(false);
  const whySectionRef = useRef<HTMLElement | null>(null);
  const whyItemRefs = useRef<Array<HTMLElement | null>>([]);
  const [language, setLanguage] = useState<'EN' | 'RU'>(() => {
    try {
      const stored = (localStorage.getItem('language') || 'en').toLowerCase();
      return stored === 'ru' ? 'RU' : 'EN';
    } catch {
      return 'EN';
    }
  });

  const lang: LandingLang = language === 'RU' ? 'ru' : 'en';
  const t = (key: LandingTranslationKey) => tLanding(lang, key);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  // "Why choose Swarmind..." reveal animation (match prototype behavior)
  useEffect(() => {
    const el = whySectionRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          setWhyVisible(true);
          whyItemRefs.current.forEach((itemRef, index) => {
            if (!itemRef) return;
            window.setTimeout(() => {
              itemRef.classList.add('visible');
            }, index * 150);
          });
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const apiModelCards = useMemo(
    () => [
      { key: 'gemma', name: 'Gemma', icon: '/icons/ai_icons/gemma-color.svg' },
      { key: 'deepseek', name: 'DeepSeek', icon: '/icons/ai_icons/deepseek.svg' },
      { key: 'openai', name: 'OpenAI', icon: '/icons/ai_icons/openai.svg' },
      { key: 'qwen', name: 'Qwen', icon: '/icons/ai_icons/qwen.svg' },
      { key: 'dreamshaper', name: 'DreamShaper', icon: '/icons/ai_icons/dreamshaper.jpg' },
    ],
    []
  );

  const scrollModelsByOne = (dir: -1 | 1) => {
    const container = modelsCarouselRef.current;
    if (!container) return;
    const track = container.querySelector<HTMLElement>('.api-models-scroll');
    const card = container.querySelector<HTMLElement>('.api-model-card');
    if (!track || !card) return;

    const gap = parseFloat((getComputedStyle(track).gap || '0').toString()) || 0;
    const cardWidth = card.getBoundingClientRect().width;
    container.scrollBy({ left: dir * (cardWidth + gap), behavior: 'smooth' });
  };

  const renderModelsTitle = () => {
    const title = t('unifiedAPIModelsTitle');
    return highlightLandingText(title, ['AI solutions store', 'магазин AI решений']);
  };

  const toggleLanguage = () => {
    const next = language === 'EN' ? 'RU' : 'EN';
    setLanguage(next);
    try {
      localStorage.setItem('language', next.toLowerCase());
    } catch {
      // ignore
    }
  };

  return (
    <div
      className={`swarmLandingRoot ${isDark ? '' : 'light-theme'}`}
      style={
        {
          ['--faq-offset' as any]: isFaqCollapsed ? '64px' : '360px',
          ['--page-accent' as any]: '#7a80d0',
          ['--page-accent-dark' as any]: '#4d52b0',
        } as React.CSSProperties
      }
    >
      <FaqSidebar t={t} accentColor="#7a80d0" onCollapsedChange={setIsFaqCollapsed} />
      <LandingSeo
        title={t('unifiedAPITitle')}
        description={t('unifiedAPISubtitle')}
        path="/unified-api"
        lang={lang}
        breadcrumbs={[
          { name: '🏠 Home', path: '/' },
          { name: '🧩 Unified API', path: '/unified-api' },
        ]}
      />
      <LandingTopNav t={t} />

      <div className="landing-main">
      <div className="page page-unified-api">
        <div className="unified-api-background">
          <ConcentricCirclesBackground enabled={motionEnabled} reduceMotion={reduceMotion} />
        </div>
        <section className="hero">
          <div className="hero-background" />
          <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <div className="hero-content">
              <div className="hero-text">
                <h1>{highlightLandingText(t('unifiedAPITitle'), ['API', 'AI'])}</h1>
                <h2>{highlightLandingText(t('unifiedAPISubtitle'), ['Fast', 'simple', 'Быстрый', 'простой'])}</h2>
                <div className="hero-buttons">
                  <button className="cta-button" onClick={() => setIsAccessOpen(true)} type="button">
                    {t('getAccess')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="api-features">
          <div className="container">
            <h2 className="api-features-title">
              {highlightLandingText(t('unifiedAPIFeaturesTitle'), ['Fast', 'simple', 'AI', 'Быстрый', 'простой'])}
            </h2>
            <div className="api-features-grid">
              <div className="api-feature-card">
                <div className="api-feature-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="api-feature-title">{t('unifiedAPIFeature1Title')}</h3>
                <p className="api-feature-desc">{t('unifiedAPIFeature1Desc')}</p>
                <a
                  href={docsUrl}
                  className="learn-more-link"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('documentation')}
                </a>
              </div>
              <div className="api-feature-card">
                <div className="api-feature-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="api-feature-title">{t('unifiedAPIFeature2Title')}</h3>
                <p className="api-feature-desc">{t('unifiedAPIFeature2Desc')}</p>
              </div>
              <div className="api-feature-card">
                <div className="api-feature-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <h3 className="api-feature-title">{t('unifiedAPIFeature3Title')}</h3>
                <p className="api-feature-desc">{t('unifiedAPIFeature3Desc')}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="api-models-gallery" ref={modelsSectionRef as any} style={{ position: 'relative', overflow: 'hidden' }}>
          <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <h2 className="api-models-title">{renderModelsTitle()}</h2>

            <div className="api-models-wrapper">
              <button
                className="api-models-nav-btn api-models-nav-left"
                onClick={() => scrollModelsByOne(-1)}
                type="button"
                aria-label="Scroll left"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="api-models-container" ref={modelsCarouselRef}>
                <div className="api-models-scroll">
                  {apiModelCards.map(card => (
                    <div key={card.key} className={`api-model-card api-model-card--${card.key}`}>
                      <img
                        className="api-model-card__icon"
                        src={`${process.env.PUBLIC_URL}${card.icon}`}
                        alt=""
                        loading="lazy"
                        aria-hidden="true"
                      />
                      <div className="api-model-card__name">{card.name}</div>
                    </div>
                  ))}
                </div>
              </div>
              <button
                className="api-models-nav-btn api-models-nav-right"
                onClick={() => scrollModelsByOne(1)}
                type="button"
                aria-label="Scroll right"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <div className="api-models-cta">
              <button className="cta-button" onClick={() => setIsAccessOpen(true)} type="button">
                {t('getAccess')}
              </button>
            </div>
          </div>
        </section>

        <section className="api-why" ref={whySectionRef}>
          <div className="container">
            <h2 className={`api-why-title ${whyVisible ? 'visible' : ''}`}>
              {highlightLandingText(t('unifiedAPIWhyTitle'), ['AI'])}
            </h2>
            <div className="api-why-items">
              <div className="api-why-item" ref={el => { whyItemRefs.current[0] = el; }}>
                <h3 className="api-why-item-title">{t('unifiedAPIWhy1Title')}</h3>
                <p className="api-why-item-desc">{t('unifiedAPIWhy1Desc')}</p>
              </div>
              <div className={`api-why-divider-wrapper ${whyVisible ? 'visible' : ''}`}>
                <div className="api-why-divider api-why-divider-title" />
                <div className="api-why-divider api-why-divider-desc" />
              </div>
              <div className="api-why-item" ref={el => { whyItemRefs.current[1] = el; }}>
                <h3 className="api-why-item-title">{t('unifiedAPIWhy2Title')}</h3>
                <p className="api-why-item-desc">{t('unifiedAPIWhy2Desc')}</p>
              </div>
              <div className={`api-why-divider-wrapper ${whyVisible ? 'visible' : ''}`}>
                <div className="api-why-divider api-why-divider-title" />
                <div className="api-why-divider api-why-divider-desc" />
              </div>
              <div className="api-why-item" ref={el => { whyItemRefs.current[2] = el; }}>
                <h3 className="api-why-item-title">{t('unifiedAPIWhy3Title')}</h3>
                <p className="api-why-item-desc">{t('unifiedAPIWhy3Desc')}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="api-how-to">
          <div className="container">
            <h2 className="api-how-to-title">
              {highlightLandingText(t('unifiedAPIHowToTitle'), ['start working', 'Swarmind', 'начать работу'])}
            </h2>
            <div className="api-how-to-grid">
              <div className="api-how-to-card">
                <div className="api-how-to-icon" aria-hidden="true">
                  <span>1</span>
                </div>
                <h3 className="api-how-to-card-title">{t('unifiedAPIHowTo1Title')}</h3>
                <p className="api-how-to-card-desc">{t('unifiedAPIHowTo1Desc')}</p>
              </div>
              <div className="api-how-to-card">
                <div className="api-how-to-icon" aria-hidden="true">
                  <span>2</span>
                </div>
                <h3 className="api-how-to-card-title">{t('unifiedAPIHowTo2Title')}</h3>
                <p className="api-how-to-card-desc">{t('unifiedAPIHowTo2Desc')}</p>
              </div>
              <div className="api-how-to-card">
                <div className="api-how-to-icon" aria-hidden="true">
                  <span>3</span>
                </div>
                <h3 className="api-how-to-card-title">{t('unifiedAPIHowTo3Title')}</h3>
                <p className="api-how-to-card-desc">{t('unifiedAPIHowTo3Desc')}</p>
              </div>
              <div className="api-how-to-card">
                <div className="api-how-to-icon" aria-hidden="true">
                  <span>4</span>
                </div>
                <h3 className="api-how-to-card-title">{t('unifiedAPIHowTo4Title')}</h3>
                <p className="api-how-to-card-desc">{t('unifiedAPIHowTo4Desc')}</p>
              </div>
            </div>
            <div className="api-how-to-cta">
              <button className="cta-button" onClick={() => setIsAccessOpen(true)} type="button">
                {t('getAccess')}
              </button>
            </div>
          </div>
        </section>
      </div>

      </div>

      <LandingFooter
        t={t}
        language={language}
        onToggleLanguage={toggleLanguage}
        isLightTheme={!isDark}
        onToggleTheme={() => setIsDark(!isDark)}
        onGetAccess={() => setIsAccessOpen(true)}
      />

      <GetAccessModal lang={lang} isOpen={isAccessOpen} onClose={() => setIsAccessOpen(false)} />
    </div>
  );
};

export default UnifiedApiLandingPage;

